import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const DownloadsSection = () => {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDownloads();
  }, []);

  const fetchDownloads = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'brochure_leads'));
      const downloadsData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        downloadsData.push({
          id: doc.id,
          companyName: data.companyName || 'N/A',
          contactPerson: data.contactPersonName || 'N/A',
          email: data.businessEmail || 'N/A',
          phone: data.contactNumber || 'N/A',
          message: data.additionalMessage || 'N/A',
          submissionDate: data.submissionDate?.toDate?.() || new Date(),
          leadStatus: data.leadStatus || 'new',
          leadSource: data.leadSource || 'website',
          brochureType: data.brochureType || 'complete_catalog',
          leadScore: data.leadScore || 'medium',
          businessCategory: data.businessCategory || 'general',
          ...data
        });
      });
      
      // Sort by submission date (newest first)
      downloadsData.sort((a, b) => new Date(b.submissionDate) - new Date(a.submissionDate));
      
      setDownloads(downloadsData);
    } catch (error) {
      console.error('Error fetching downloads:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDownloads = downloads.filter(download =>
    download.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    download.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    download.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    download.businessCategory?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Handle Firestore timestamp format
    if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString() + ' ' + timestamp.toDate().toLocaleTimeString();
    }
    
    // Handle string format like "September 4, 2025 at 1:24:24 PM UTC+5:30"
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
      }
    }
    
    // Handle regular Date object
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString() + ' ' + timestamp.toLocaleTimeString();
    }
    
    return 'N/A';
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'catalog': return 'bg-blue-100 text-blue-800';
      case 'brochure': return 'bg-green-100 text-green-800';
      case 'corporate_gifting': return 'bg-purple-100 text-purple-800';
      case 'general': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const totalDownloads = downloads.length;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Brochure Download Leads</h2>
        <p className="text-gray-600">Track and manage brochure download requests</p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
          <input
            type="text"
            placeholder="Search leads by company, contact, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">Total Leads</p>
          <p className="text-2xl font-bold text-blue-600">{totalDownloads.toLocaleString()}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">Hot Leads</p>
          <p className="text-2xl font-bold text-green-600">
            {downloads.filter(d => d.leadScore === 'hot').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">Corporate Leads</p>
          <p className="text-2xl font-bold text-purple-600">
            {downloads.filter(d => d.businessCategory === 'corporate_gifting').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">Today's Leads</p>
          <p className="text-2xl font-bold text-orange-600">
            {downloads.filter(download => {
              const submissionDate = download.submissionDate;
              if (!submissionDate) return false;
              
              const today = new Date();
              const downloadDate = new Date(submissionDate);
              return downloadDate.toDateString() === today.toDateString();
            }).length}
          </p>
        </div>
      </div>

      {/* Top Files Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Downloads</h3>
        <div className="space-y-4">
          {downloads.slice(0, 3).map((download, index) => (
            <div key={download.id} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    index === 0 ? 'bg-yellow-100 text-yellow-600' :
                    index === 1 ? 'bg-gray-100 text-gray-600' :
                    'bg-orange-100 text-orange-600'
                  }`}>
                    {index + 1}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{download.fileName}</p>
                  <p className="text-xs text-gray-500">{download.fileSize}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{download.downloadCount}</p>
                <p className="text-xs text-gray-500">downloads</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Downloads Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company & Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submission Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Message
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDownloads.map((download) => (
                <tr key={download.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                        </svg>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{download.companyName}</div>
                        <div className="text-sm text-gray-500">{download.contactPerson}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{download.email}</div>
                    <div className="text-sm text-gray-500">{download.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(download.businessCategory)}`}>
                      {download.businessCategory}
                    </span>
                    <div className="text-xs text-gray-500 mt-1">
                      Score: {download.leadScore} | {download.leadSource}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(download.submissionDate)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate" title={download.message}>
                      {download.message}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredDownloads.length === 0 && (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No downloads found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search terms.' : 'Download analytics will appear here.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadsSection;
